import { useNavigate } from 'react-router-dom'

import hero1440 from '../../assets/landing/hero-1440.webp'
import hero1440x2 from '../../assets/landing/hero-1440@2x.webp'
import hero360 from '../../assets/landing/hero-360.webp'
import hero360x2 from '../../assets/landing/hero-360@2x.webp'
import { Arrow, CheckDot, Eyebrow } from './icons'

const CHECKS = ['Instant ARV with scored comps', 'See projected profit upfront', 'Track every deal in one place']

// Figma 29.09 `hero` / `hero-txt*` + `img-hero`, spec §2.4. Vertical steps are the export's
// baselines converted to line boxes; they follow the new design, which supersedes the earlier
// review corrections (72 from the banner, H1 line height 72) per letter 97 p.5.
const Hero = () => {
  const navigate = useNavigate()

  return (
    <section className="relative bg-brand-pageFill">
      <div className="relative max-w-[640px] canvas:max-w-canvas mx-auto px-5 canvas:px-0 pt-6 canvas:pt-7">
        <div className="relative z-10 canvas:w-[515px]">
          <Eyebrow>
            <span className="canvas:hidden">Deal Analysis for Real Estate Investors</span>
            <span className="hidden canvas:inline">Deal Analysis for Investors &amp; Wholesalers</span>
          </Eyebrow>

          {/* Letter-spacing is not in the export (spec M5); the live tracking is kept. */}
          <h1 className="mt-4 canvas:mt-[13px] text-[28px] leading-9 canvas:text-[52px] canvas:leading-[64px] font-extrabold tracking-[-1px] canvas:tracking-[-2px]">
            <span className="text-brand-dark">Know the Deal.</span>
            <br />
            <span className="text-brand-accent">Before You Invest.</span>
          </h1>

          {/* D1: the two frames word this differently; each width keeps its own copy. */}
          <p className="mt-4 canvas:mt-6 text-[18px] leading-7 canvas:text-[20px] canvas:leading-9 text-brand-dark">
            Analyze a property in under five minutes and know{' '}
            <span className="canvas:hidden">if</span>
            <span className="hidden canvas:inline">whether</span> it&rsquo;s worth pursuing.
          </p>

          <ul className="mt-[18px] canvas:mt-6 flex flex-col gap-[10px] canvas:gap-[6px]">
            {CHECKS.map((item) => (
              <li key={item} className="flex items-center gap-[6px] canvas:gap-3">
                <CheckDot dotClass="bg-brand-accent" ringClass="w-6 h-6 canvas:w-7 canvas:h-7 bg-[#CBF7B7]" />
                <span className="text-[18px] leading-6 canvas:text-[20px] canvas:leading-9 text-brand-dark">{item}</span>
              </li>
            ))}
          </ul>

          {/* No shadow or hover is drawn (spec M6); the live ones are kept. */}
          <button
            type="button"
            onClick={() => navigate('/signup')}
            className="mt-[18px] canvas:mt-6 w-full canvas:w-auto h-14 canvas:pl-10 canvas:pr-[29px] rounded-full bg-brand-primary hover:bg-brand-hover text-white text-[16px] font-semibold shadow-soft transition-colors inline-flex items-center justify-center gap-3"
          >
            Start Your Free Trial
            <Arrow />
          </button>

          {/* Letter 97 p.4: "7-days" here (the frames draw "7 days"). */}
          <p className="mt-[13px] canvas:mt-[7px] text-[12px] leading-4 canvas:text-[14px] canvas:leading-5 text-brand-dark text-center canvas:text-left">
            7-days · Up to 5 properties · No credit card required
          </p>
        </div>
      </div>

      {/* Desktop: 720 x 660 at frame (720, 180), flush with the right edge at 1440. Anchored to the
          viewport's right edge (from 1440, the 1440 frame's edge, so it stays with the column on wider
          screens) and narrowed (50% + 65px) so it never runs under the 515px text
          column between 1232 and 1440. Below 1232 it follows the text in the stacked column, and from
          640 up it uses the desktop art, which stays sharp at the 640px column width. */}
      <picture>
        <source media="(min-width: 640px)" srcSet={`${hero1440} 720w, ${hero1440x2} 1440w`} sizes="(min-width: 1232px) 720px, 640px" />
        <img
          src={hero360}
          srcSet={`${hero360} 360w, ${hero360x2} 720w`}
          sizes="(min-width: 640px) 640px, 100vw"
          alt="Arvance property analysis: ARV estimate, deal score and comps map"
          width="360"
          height="410"
          className="block mx-auto mt-[-1px] w-full max-w-[640px] h-auto canvas:absolute canvas:mt-0 canvas:right-0 min-[1440px]:right-[calc(50%-720px)] canvas:top-7 canvas:w-[min(720px,calc(50%+65px))] canvas:max-w-none"
        />
      </picture>
    </section>
  )
}

export default Hero
